import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HeroSection = styled.section`
  height: 100vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  background-color: var(--color-primary-dark);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(rgba(28, 43, 58, 0.85), rgba(28, 43, 58, 0.95)),
      url('/images/blueprint-bg.jpg');
    background-size: cover;
    background-position: center;
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  width: 100%;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  color: var(--color-light);
  margin-bottom: 1.5rem;
  line-height: 1.2;
  max-width: 800px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: var(--color-gray);
  margin-bottom: 2.5rem;
  max-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: var(--color-accent);
  color: var(--color-white);
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    background: var(--color-accent);
    opacity: 0.9;
    transform: translateY(-2px);
    color: var(--color-white);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: transparent;
  color: var(--color-light);
  font-weight: 600;
  border-radius: 4px;
  border: 2px solid var(--color-light);
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    background: var(--color-light);
    color: var(--color-primary-dark);
  }
`;

const ServicesSection = styled.section`
  padding: 6rem 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const ServiceCard = styled.div`
  background: var(--color-white);
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-accent);
  margin-bottom: 1.5rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: var(--color-primary);
  margin-bottom: 1.5rem;
`;

const AboutSection = styled.section`
  padding: 6rem 0;
  background-color: var(--color-gray);
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutText = styled.div`
  @media (max-width: 768px) {
    order: 2;
  }
`;

const AboutTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AboutDescription = styled.p`
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const AboutImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  height: 100%;
  min-height: 400px;
  
  @media (max-width: 768px) {
    order: 1;
    min-height: 300px;
  }
`;

const AboutImage = styled.div`
  background-image: url('/images/architecture-diagram.svg');
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 100%;
`;

const CtaSection = styled.section`
  padding: 5rem 0;
  background-color: var(--color-accent);
  color: var(--color-white);
  text-align: center;
`;

const CtaContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CtaTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CtaDescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CtaButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2.5rem;
  background: var(--color-white);
  color: var(--color-accent);
  font-weight: 700;
  font-size: 1.1rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-primary-dark);
    color: var(--color-white);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const Home = () => {
  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeroTitle className="fade-in">
            Architecting Intelligent & Resilient Technology Solutions
          </HeroTitle>
          <HeroSubtitle className="fade-in delay-1">
            We transform complexity into clarity, guiding organizations through innovation with precision, integrity, and a relentless focus on quality.
          </HeroSubtitle>
          <ButtonGroup>
            <PrimaryButton to="/services" className="fade-in delay-2">
              Explore Services
            </PrimaryButton>
            <SecondaryButton to="/contact" className="fade-in delay-3">
              Get in Touch
            </SecondaryButton>
          </ButtonGroup>
        </HeroContent>
      </HeroSection>

      <ServicesSection>
        <div className="container">
          <h2 className="section-title">Our Core Services</h2>
          <ServicesGrid>
            <ServiceCard>
              <ServiceIcon>
                <i className="fas fa-sitemap"></i>
              </ServiceIcon>
              <ServiceTitle>Architecture</ServiceTitle>
              <ServiceDescription>
                Strategic system design and technology selection aligned with your business goals and growth trajectory.
              </ServiceDescription>
              <Link to="/services#architecture" style={{ marginTop: 'auto' }}>
                Learn More &rarr;
              </Link>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <i className="fas fa-code-branch"></i>
              </ServiceIcon>
              <ServiceTitle>System Design</ServiceTitle>
              <ServiceDescription>
                Crafting scalable, maintainable systems that stand up to evolving business needs and technical challenges.
              </ServiceDescription>
              <Link to="/services#system-design" style={{ marginTop: 'auto' }}>
                Learn More &rarr;
              </Link>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <i className="fas fa-users-cog"></i>
              </ServiceIcon>
              <ServiceTitle>Consulting</ServiceTitle>
              <ServiceDescription>
                Expert guidance on technology decisions, implementation strategies, and best practices for your unique context.
              </ServiceDescription>
              <Link to="/services#consulting" style={{ marginTop: 'auto' }}>
                Learn More &rarr;
              </Link>
            </ServiceCard>
          </ServicesGrid>
        </div>
      </ServicesSection>

      <AboutSection>
        <AboutContent>
          <AboutText>
            <AboutTitle>About Kriegerlabs</AboutTitle>
            <AboutDescription>
              Kriegerlabs exists to bridge the gap between vision and execution, bringing clarity to complex technical challenges through thoughtful architecture and strategic design.
            </AboutDescription>
            <AboutDescription>
              With a focus on intelligent, resilient solutions, we help organizations navigate technical complexity and build systems that stand the test of time and scale.
            </AboutDescription>
            <PrimaryButton to="/about" style={{ marginTop: '1rem' }}>
              Our Approach
            </PrimaryButton>
          </AboutText>
          
          <AboutImageContainer>
            <AboutImage />
          </AboutImageContainer>
        </AboutContent>
      </AboutSection>

      <CtaSection>
        <CtaContainer>
          <CtaTitle>Ready to Transform Your Technical Vision?</CtaTitle>
          <CtaDescription>
            Let's discuss how we can help architect intelligent solutions for your organization's unique challenges.
          </CtaDescription>
          <CtaButton to="/contact">
            Start a Conversation
          </CtaButton>
        </CtaContainer>
      </CtaSection>
    </>
  );
};

export default Home;